import { useState, useEffect } from "react";
import {
  getOptionListApi,
  getPructureProtectionApi,
  updateOptionApi,
  updatePructureProtectionApi,
} from "../api/optionApi";
import {
  MstItemOptionUpdate,
  MstItemPructureProtectUpdate,
  OptionItem,
  PunctureProtection,
} from "../types/option";

/**
 * Custom hook to fetch and manage a paginated list of companies.
 * This hook uses a placeholder for the actual API call. Replace the commented-out fetchClients function with the actual API call to fetch companies.
 */
const useOptionList = () => {
  const [loading, setLoading] = useState(false);
  const [natOptions, setNatOptions] = useState<OptionItem[]>([]);
  const [lockNatOptions, setLockNatOptions] = useState<OptionItem[]>([]);
  const [hubOptions, setHubOptions] = useState<OptionItem[]>([]);
  const [totalOption, setTotalOption] = useState<number>();
  const [totalProtection, setTotalProtection] = useState<number>();
  const [protectionOptions, setProtectionOptions] = useState<PunctureProtection[]>([]);
  const [originalOptions, setOriginalOptions] = useState<OptionItem[]>([]);
  const [originalProtectionOptions, setOriginalProtectionOptions] = useState<PunctureProtection[]>(
    [],
  );

  const getOptions = async () => {
    try {
      const [natRes, locknatRes, hubRes] = await Promise.all([
        getOptionListApi({ mode: "nat" }),
        getOptionListApi({ mode: "locknat" }),
        getOptionListApi({ mode: "hub" }),
      ]);
      setNatOptions(natRes.data);
      setLockNatOptions(locknatRes.data);
      setHubOptions(hubRes.data);
      setOriginalOptions([...natRes.data, ...locknatRes.data, ...hubRes.data]);
      setTotalOption(natRes.total + locknatRes.total + hubRes.total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  const checkShouldUpdate = (data: MstItemOptionUpdate) => {
    if (!data.id) return false;
    const originalOption = originalOptions.find((option) => option.id === data.id);
    if (!originalOption) return false;
    const isSame = Object.entries(data).every(([key, value]) => {
      if (key === "id") return true;
      const originalValue = originalOption[key as keyof OptionItem];
      if (originalValue === value) {
        return true;
      }
      setOriginalOptions((prevOptions) =>
        prevOptions.map((option) => {
          if (option.id === data.id) {
            return { ...option, [key]: value };
          }
          return option;
        }),
      );
      return false;
    });
    return !isSame;
  };

  const checkShouldUpdateProtection = (data: MstItemPructureProtectUpdate) => {
    if (!data.id) return false;
    const originalProtection = originalProtectionOptions.find((option) => option.id === data.id);
    if (!originalProtection) return false;
    const isSame = Object.entries(data).every(([key, value]) => {
      if (key === "id") return true;
      const originalValue = originalProtection[key as keyof PunctureProtection];
      if (originalValue === value) {
        return true;
      }
      setOriginalProtectionOptions((prevOptions) =>
        prevOptions.map((option) => {
          if (option.id === data.id) {
            return { ...option, [key]: value };
          }
          return option;
        }),
      );
      return false;
    });
    return !isSame;
  };

  const getPunctureProtections = async () => {
    try {
      const { list, total } = await getPructureProtectionApi();
      setProtectionOptions(list);
      setOriginalProtectionOptions(list);
      setTotalProtection(total);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  const updateOption = async (body: MstItemOptionUpdate): Promise<void> => {
    try {
      if (!checkShouldUpdate(body)) return;
      const { result } = await updateOptionApi(body);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  const updatePructureProtection = async (body: MstItemPructureProtectUpdate): Promise<void> => {
    try {
      if (!checkShouldUpdateProtection(body)) return;
      const { result } = await updatePructureProtectionApi(body);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  useEffect(() => {
    getOptions();
    getPunctureProtections();
  }, []);

  return {
    loading,
    natOptions,
    lockNatOptions,
    hubOptions,
    totalOption,
    protectionOptions,
    totalProtection,
    setNatOptions,
    setLockNatOptions,
    setHubOptions,
    setProtectionOptions,
    updateOption,
    updatePructureProtection,
  };
};

export default useOptionList;
